import PropTypes from "prop-types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { mutate } from "swr";
import { useUser } from "../../../globalState";
import {
  shopOpenNoticeKey,
  useInfluencer,
  useShippingAddressInfluencerId,
  useUserAccount,
} from "../../../hooks";
import { useLivestreamWarningContext } from "../../../pages/Dashboard/ShopEditor";
import { setShopStatus } from "../../../services/shopStatus";
import { Button, Dialog, Switch, Typography } from "../../../themeV2";
import { compose, isEmpty, or, pick, some, values } from "../../../util";
import NotifyActionsModal from "../../NotifyActionsModal";
import css from "./ShopHero.module.css";
import { dashboardPath } from "../../../domain/dashboardPaths";

const SHIPPING_COLUMNS = [
  "first_name",
  "last_name",
  "address_primary",
  "city",
  "state",
  "country",
  "zip_code",
];

const defaultEmail = "partnerships@streamily.com";

const ShopStatus = React.memo(function ShopStatus({ route }) {
  const currentUser = useUser();
  const [isLoading, setIsLoading] = React.useState(false);
  const history = useHistory();
  const { influencer, refresh } = useInfluencer({
    route,
    forShopBuilder: true,
  });
  const { account } = useUserAccount(influencer?.userId);
  const { shippingAddress } = useShippingAddressInfluencerId(
    influencer?.influencerId,
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const livestreamWarning = useLivestreamWarningContext();
  const location = useLocation();
  const [notifyProps, setNotifyProps] = React.useState(null);
  const [closedShopDialogOpen, setClosedShopDialogOpen] = React.useState(false);

  const onChange = React.useCallback(() => {
    const nextIsOpen = !influencer?.isOpen;

    setIsLoading(true);
    setIsOpen(nextIsOpen);

    setShopStatus({
      influencerId: influencer?.influencerId,
      isOpen: nextIsOpen,
    })
      .then((result) => {
        if (result?.errors) {
          setNotifyProps({
            type: "shopSetupError",
            title: "Oops, shop still needs some set-up!",
            message: result.message,
            actions: [],
            customActions: ({ onClose }) => (
              <div className={css.notifyActions}>
                <Button variant="filled" onClick={onClose}>
                  OK
                </Button>
              </div>
            ),
          });
        } else {
          refresh();
          mutate(shopOpenNoticeKey(influencer?.influencerId));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [influencer?.isOpen, influencer?.influencerId, refresh]);

  React.useEffect(() => {
    if (location.state?.eventUpdated) {
      if (influencer) {
        mutate(`/api/influencerEvents/${influencer.influencerId}`);
      }
      history.replace(location.pathname, {});
    }
  }, [location.state?.eventUpdated, influencer, history, location.pathname]);

  const verifyChange = React.useCallback(() => {
    if (isOpen || influencer.isGroupShop) {
      // closing shop, no tests
      // collections have no tests
      onChange();
      return;
    }

    const hasShippingError = compose(
      or(some(isEmpty), isEmpty),
      values,
      pick(SHIPPING_COLUMNS),
    )(shippingAddress);

    if (account && hasShippingError) {
      setNotifyProps({
        type: "shippingError",
        title: "Oops, we need your shipping address!",
        message:
          "Please fill out your shipping address in Account Settings before opening your shop. This is where we will send your prints for your signing, please make sure it's correct!",
        actions: ["Go to Account Settings"],
      });
    } else if (livestreamWarning) {
      setNotifyProps({
        type: "livestreamError",
        title: "Oops! Looks like you don't have an event set",
        message:
          "Your Signed on Livestream products won't be shown to customers until you set an event.",
        actions: [
          "Schedule An Event",
          "Open my shop without Signed on Livestream products",
        ],
      });
    } else {
      onChange();
    }
  }, [
    account,
    influencer,
    onChange,
    isOpen,
    livestreamWarning,
    shippingAddress,
  ]);

  const notifyModalSave = React.useCallback(
    (actionIndex, actionText) => {
      if (actionText.indexOf("Account Settings") >= 0) {
        history.push(dashboardPath({ shop: route, page: "settings" }));
      } else if (actionText.includes("shop split")) {
        // do nothing
      } else if (actionText.includes("shop owner")) {
        // do nothing
      } else {
        // livestreamWarning
        if (actionIndex === 0) {
          history.push(dashboardPath({ shop: route, page: "events/new" }), {
            returnTo: location.pathname,
          });
        }
        if (actionIndex === 1) {
          onChange();
        }
      }
    },
    [onChange, history, location.pathname, route],
  );

  React.useEffect(() => {
    if (!isLoading) {
      setIsOpen(Boolean(influencer?.isOpen));
    }
  }, [isLoading, influencer?.isOpen]);

  const emailMailto = React.useMemo(() => {
    const email = influencer?.tcEmail || defaultEmail;
    const params = new URLSearchParams({
      subject: "Help me set up my shop!",
    });

    if (email !== defaultEmail) {
      params.set("cc", defaultEmail);
    }

    return `mailto:${email}?${params}`;
  }, [influencer?.tcEmail]);

  const emailDisplayName = React.useMemo(() => {
    const email = influencer?.tcEmail || defaultEmail;
    return email;
  }, [influencer?.tcEmail]);

  const statusText = isOpen ? "Shop open" : "Shop closed";

  return (
    <>
      {currentUser.isAdmin && (
        <Switch
          label={statusText}
          className={css.openLine}
          checked={isOpen}
          onCheckedChange={verifyChange}
          disabled={!influencer || isLoading}
        />
      )}

      {!currentUser.isAdmin && (
        <>
          <button
            type="button"
            onClick={() => {
              setClosedShopDialogOpen(true);
            }}
            className={css.openLine}
          >
            {statusText}
          </button>

          <Dialog.Root
            open={closedShopDialogOpen}
            onOpenChange={setClosedShopDialogOpen}
          >
            <Dialog.Popup size="sm">
              <Dialog.Close />
              <Dialog.Title>Want to take a break?</Dialog.Title>

              <Dialog.Description>
                You don&apos;t need to close your shop to take time off from
                signing. Keep fans engaged without lifting a pen:
              </Dialog.Description>

              <Dialog.Body>
                <ul className={css.closeDialogList}>
                  <li>
                    <strong>Unsigned merch</strong> — fans love collecting items
                    from their favorite talent
                  </li>
                  <li>
                    <strong>Pre-signed prints</strong> — a hit for those who
                    missed your live events
                  </li>
                </ul>

                <Typography
                  variant="body-md"
                  as="p"
                  className={css.closeDialogHelp}
                >
                  Want help setting this up, or have questions about your shop?
                </Typography>
              </Dialog.Body>

              <Dialog.Actions>
                <Button
                  variant="outlined"
                  onClick={() => setClosedShopDialogOpen(false)}
                >
                  Got it
                </Button>
                <Button as="a" variant="filled" href={emailMailto}>
                  Contact us
                </Button>
              </Dialog.Actions>
            </Dialog.Popup>
          </Dialog.Root>
        </>
      )}

      {notifyProps && (
        <NotifyActionsModal
          title={notifyProps.title}
          subtitle={notifyProps.message}
          open={notifyProps !== null}
          onClose={() => setNotifyProps(false)}
          onSave={notifyModalSave}
          actions={notifyProps.actions}
          customActions={notifyProps.customActions}
        />
      )}
    </>
  );
});

ShopStatus.propTypes = {
  route: PropTypes.string.isRequired,
};

export default ShopStatus;
